import React, { useState, useEffect, useRef } from 'react'
import { BUTTON_STYLES, COLORS, API_ENDPOINTS } from '../constants'
import { createButtonStyle } from '../utils'
import axios from 'axios'
import { api } from '../../../utils/variables'

const KeywordSearch = ({
  searchInput,
  setSearchInput,
  onAddKeyword,
  addingKeyword,
  selectedCountry,
  selectedLanguage,
  onGenerateKeywords,
  disabled = false,
  keywordCount,
  keywordLimit,
  planLoading,
  onLimitReached,
}) => {
  const [stagedGenres, setStagedGenres] = useState([])
  const [isHovered, setIsHovered] = useState(false)
  const [suggestions, setSuggestions] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef(null)
  const debounceTimerRef = useRef(null)

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Debounced search API call
  const fetchSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([])
      setShowDropdown(false)
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    try {
      const res = await axios.get(`${api}${API_ENDPOINTS.genreSearch}`, {
        params: { q: query },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`
        }
      })
      if (res.data?.results) {
        setSuggestions(res.data.results)
        setShowDropdown(true) // Always show if we got a response for query >= 2 chars
      } else {
        setSuggestions([])
        setShowDropdown(true)
      }
    } catch (err) {
      console.error("Genre search failed:", err)
      setSuggestions([])
      setShowDropdown(false)
    } finally {
      setIsSearching(false)
    }
  }

  const handleInputChange = (e) => {
    const value = e.target.value
    setSearchInput(value)

    // Set searching to true immediately to disable Add button during debounce/fetch
    setIsSearching(true)

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(value)
    }, 400)
  }

  const handleSuggestionClick = (genre) => {
    if (!stagedGenres.includes(genre)) {
      setStagedGenres([...stagedGenres, genre])
    }
    setSearchInput('')
    setSuggestions([])
    setShowDropdown(false)
  }

  const removeStagedGenre = (genre) => {
    setStagedGenres(stagedGenres.filter(g => g !== genre))
  }

  const handleAddClick = () => {
    let genresToAdd = [...stagedGenres]
    if (isValidGenre && !stagedGenres.includes(searchInput)) {
      genresToAdd.push(searchInput)
    }

    if (genresToAdd.length > 0) {
      onAddKeyword(genresToAdd.join(','))
      setStagedGenres([])
      setSearchInput('')
      setShowDropdown(false)
    }
  }

  const handleKeyDown = e => {
    if (e.key === "Enter" && selectedCountry && selectedLanguage) {
      handleAddClick()
    }
  }

  const isExactMatch = suggestions.some(s => s.toLowerCase() === (searchInput || "").toLowerCase())
  const isValidGenre = suggestions.length > 0 && isExactMatch

  // Base disabled state for input (basics like country/lang selection)
  const isBaseDisabled = addingKeyword || !selectedCountry || !selectedLanguage || disabled

  // Button is enabled if we have staged genres OR a valid exact match in the search input
  const isButtonDisabled = isBaseDisabled || (stagedGenres.length === 0 && (isSearching || !isValidGenre))

  const isLimitReached = keywordLimit !== 'Unlimited' && keywordCount >= parseInt(keywordLimit)

  console.log('KeywordSearch Debug:', { keywordCount, keywordLimit, isLimitReached })

  const handleInputClick = () => {
    if (isLimitReached && onLimitReached) {
      console.log('Limit reached, triggering upgrade modal')
      onLimitReached()
    }
  }

  const getPlaceholderText = () => {
    if (isLimitReached) {
      return `Genre limit reached (${keywordLimit}/${keywordLimit})`
    }
    if (!selectedCountry && !selectedLanguage) {
      return "Select country and language first"
    } else if (!selectedCountry) {
      return "Select a country first"
    } else if (!selectedLanguage) {
      return "Select a language first"
    }
    return "Enter the genre"
  }

  const buttonStyle = createButtonStyle(
    isHovered,
    {
      ...BUTTON_STYLES.addButton,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      padding: "10px 20px",
      minWidth: "120px",
      height: "40px",
      fontSize: "14px",
      fontWeight: "500",
      opacity: (isButtonDisabled || isLimitReached) ? 0.5 : 1,
      cursor: (isButtonDisabled || isLimitReached) ? "not-allowed" : "pointer",
      pointerEvents: (isButtonDisabled || isLimitReached) ? "none" : "auto",
      zIndex: 10,
      position: "relative",
    },
    (isButtonDisabled || isLimitReached) ? "#64748b" : COLORS.primary,
    (isButtonDisabled || isLimitReached) ? "#475569" : COLORS.primaryHover
  )

  return (
    <div className="d-flex flex-column gap-2">
      {!selectedCountry || !selectedLanguage ? (
        <div
          style={{
            fontSize: "13px",
            color: "#94a3b8",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            marginBottom: "8px",
          }}
        >
          <i className="fa-solid fa-info-circle" style={{ fontSize: "12px" }} />
          Please select country and language above to continue
        </div>
      ) : null}

      {/* SEARCH ROW - Responsive Layout */}
      <div
        className="d-flex gap-3"
        style={{
          flexDirection: window.innerWidth < 768 ? "column" : "row",
          alignItems: window.innerWidth < 768 ? "stretch" : "center"
        }}
      >
        {/* INPUT CONTAINER - Full width on small screens */}
        <div
          style={{
            flex: 1, // Full width on desktop
            width: window.innerWidth < 768 ? "100%" : "auto"
          }}
        >
          <div
            className="d-flex align-items-center flex-wrap"
            style={{
              flex: 1,
              minWidth: 0,
              position: 'relative',
              background: "transparent",
              border: "1px solid #3A3B47",
              borderRadius: "6px",
              padding: "4px 10px",
              minHeight: "40px"
            }}
          >
            <i
              className="fas fa-magnifying-glass"
              // Same global-CSS overlap fix as KeywordTracking's
              // KeywordSearch.jsx — the shared `.fa-magnifying-glass` rule
              // in app.scss forces position:absolute, which inside this
              // input's own position:relative wrapper pulls the icon out
              // of flow and overlaps it onto the placeholder text.
              style={{ position: "static", marginRight: "10px", zIndex: 1, color: "#94a3b8" }}
            />

            {/* Badges for staged genres */}
            {stagedGenres.map((genre, idx) => (
              <div
                key={idx}
                style={{
                  background: COLORS.primary,
                  color: COLORS.white,
                  borderRadius: '4px',
                  padding: '2px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  margin: '2px 4px 2px 0',
                  whiteSpace: 'nowrap'
                }}
              >
                {genre}
                <i
                  className="fas fa-times"
                  onClick={() => removeStagedGenre(genre)}
                  style={{ cursor: 'pointer', fontSize: '10px' }}
                />
              </div>
            ))}

            <input
              type="text"
              placeholder={stagedGenres.length > 0 ? "" : getPlaceholderText()}
              value={searchInput}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={isBaseDisabled || isLimitReached}
              onClick={handleInputClick}
              onFocus={handleInputClick}
              title={isLimitReached ? "Genre limit reached. Click to upgrade your plan." : (isBaseDisabled ? "Please select country and language first" : "")}
              style={{
                background: "transparent",
                border: "none",
                padding: "4px 0",
                fontSize: "14px",
                color: COLORS.white,
                outline: "none",
                flex: 1,
                minWidth: "120px",
                height: "30px", // Smaller height since parent controls padding
                opacity: (isBaseDisabled || isLimitReached) ? 0.5 : 1,
                cursor: isLimitReached ? "pointer" : (isBaseDisabled ? "not-allowed" : "text"),
                paddingRight: window.innerWidth < 768 ? "100px" : "120px"
              }}
            />

            {/* AI Genre Button inside input */}
            <button
              onClick={onGenerateKeywords}
              disabled={isBaseDisabled || isLimitReached}
              title={
                isLimitReached ? "Genre limit reached. Click to upgrade your plan." :
                  (isBaseDisabled ? "Select country and language first" : "Generate AI-powered genre recommendations")
              }
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: (isBaseDisabled || isLimitReached) ? '#4b5563' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: COLORS.white,
                border: 'none',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '11px',
                fontWeight: '600',
                cursor: (isBaseDisabled || isLimitReached) ? 'not-allowed' : 'pointer',
                opacity: (isBaseDisabled || isLimitReached) ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                whiteSpace: 'nowrap',
                zIndex: 1,
                boxShadow: (isBaseDisabled || isLimitReached) ? 'none' : '0 2px 8px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.2s ease'
              }}
            >
              <i
                className="fas fa-magic"
                style={{ fontSize: '10px' }}
              />
              AI Genre
            </button>

            {/* Suggestions Dropdown */}
            {showDropdown && (
              <div
                ref={dropdownRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: -1,
                  right: -1,
                  background: '#2A2B37',
                  border: '1px solid #3A3B47',
                  borderBottomLeftRadius: '6px',
                  borderBottomRightRadius: '6px',
                  marginTop: '1px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 2000,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                  padding: '4px'
                }}
              >
                {suggestions.length > 0 ? (
                  suggestions.map((suggestion, index) => {
                    const isStaged = stagedGenres.includes(suggestion);
                    return (
                      <div
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        style={{
                          padding: '10px 16px',
                          cursor: isStaged ? 'default' : 'pointer',
                          color: isStaged ? '#64748b' : COLORS.white,
                          fontSize: '14px',
                          borderRadius: '4px',
                          backgroundColor: 'transparent',
                          transition: 'background 0.2s ease',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                        onMouseEnter={(e) => {
                          if (!isStaged) e.target.style.backgroundColor = '#3A3B47';
                        }}
                        onMouseLeave={(e) => {
                          if (!isStaged) e.target.style.backgroundColor = 'transparent';
                        }}
                      >
                        <span>{suggestion}</span>
                        {isStaged && <i className="fas fa-check" style={{ fontSize: '12px', color: COLORS.primary }} />}
                      </div>
                    );
                  })
                ) : (
                  <div
                    style={{
                      padding: '12px 16px',
                      color: '#94a3b8',
                      fontSize: '13px',
                      textAlign: 'center',
                      fontStyle: 'italic'
                    }}
                  >
                    No matching genres found
                  </div>
                )}
              </div>
            )}

            {isSearching && (
              <div style={{
                position: 'absolute',
                right: "12px", // Fixed position from right
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2
              }}>
                <i className="fa fa-spinner fa-spin" style={{ color: COLORS.primary, fontSize: '14px' }} />
              </div>
            )}
          </div>
        </div>

        {/* ADD BUTTON - Only show inline on desktop */}
        {window.innerWidth >= 768 && (
          <button
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleAddClick}
            disabled={isButtonDisabled || isLimitReached}
            title={
              isLimitReached ? "Genre limit reached. Click to upgrade your plan." :
                (isButtonDisabled ? "Select genres from suggestions to add" : (isBaseDisabled ? "Select country and language first" : "Add genre"))
            }
            style={{
              ...buttonStyle,
              padding: "10px 20px",
              minWidth: "120px",
              maxWidth: "150px",
              height: "40px",
              flexShrink: 0,
              fontSize: "14px"
            }}
          >
            <span>{stagedGenres.length > 1 ? `Add ${stagedGenres.length}` : 'Add'}</span>
            {addingKeyword && (
              <i
                className="fa fa-spinner fa-spin"
                style={{
                  fontSize: "16px",
                  color: COLORS.white,
                  marginLeft: "8px",
                }}
              />
            )}
          </button>
        )}

        {/* ADD AND IMPORT BUTTONS ROW - Only on small screens */}
        {window.innerWidth < 768 && (
          <div
            className="d-flex gap-2 mt-3"
            style={{ justifyContent: "center" }}
          >
            {/* Add Button */}
            <button
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={handleAddClick}
              disabled={isButtonDisabled || isLimitReached}
              title={
                isLimitReached ? "Genre limit reached. Click to upgrade your plan." :
                  (isButtonDisabled ? "Select genres from suggestions to add" : (isBaseDisabled ? "Select country and language first" : "Add genre"))
              }
              style={{
                ...buttonStyle,
                padding: "10px 20px",
                minWidth: "120px",
                height: "40px",
                fontSize: "14px",
                flex: 1
              }}
            >
              <span>{stagedGenres.length > 1 ? `Add ${stagedGenres.length}` : 'Add'}</span>
              {addingKeyword && (
                <i
                  className="fa fa-spinner fa-spin"
                  style={{
                    fontSize: "16px",
                    color: COLORS.white,
                    marginLeft: "8px",
                  }}
                />
              )}
            </button>

            {/* Import Button - Icon Only */}
            <button
              onClick={() => {
                // This will be handled by the parent component
                if (window.onImportClick) {
                  window.onImportClick()
                }
              }}
              disabled={isBaseDisabled || isLimitReached}
              title={isLimitReached ? "Genre limit reached. Click to upgrade your plan." : "Import Genres"}
              style={{
                backgroundColor: (isBaseDisabled || isLimitReached) ? '#4b5563' : COLORS.primary,
                color: COLORS.white,
                padding: "10px",
                borderRadius: "4px",
                border: "none",
                cursor: (isBaseDisabled || isLimitReached) ? 'not-allowed' : 'pointer',
                fontSize: "14px",
                width: "40px",
                height: "40px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: (isBaseDisabled || isLimitReached) ? 0.6 : 1,
                transition: "all 0.2s ease",
                flexShrink: 0
              }}
            >
              <i
                className="fas fa-upload"
                style={{ fontSize: "12px" }}
              ></i>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default KeywordSearch